'use client';

import { CheckCircledIcon } from '@radix-ui/react-icons';
import { useMutation } from '@tanstack/react-query';

import { useSupabase } from '@kit/supabase/hooks/use-supabase';
import { Alert, AlertDescription, AlertTitle } from '@kit/ui/alert';
import { Button } from '@kit/ui/button';
import { If } from '@kit/ui/if';
import { Trans } from '@kit/ui/trans';

/**
 * @name VerifyEmailPanel
 * @description
 * Terminal state of email sign-up: the account exists but is unusable until
 * the visitor opens the link.
 *
 * It replaces the sign-up form rather than sitting under it, and it carries
 * everything needed to get unstuck without going back to a dead form — the
 * address the link actually went to, a way to send it again, and a way to
 * correct a typo in the address.
 */
export function VerifyEmailPanel(props: {
  email: string;
  emailRedirectTo: string;
  onChangeEmail: () => void;
}) {
  const resend = useResendConfirmationEmail();

  return (
    <div className={'flex w-full flex-col gap-y-4'}>
      <Alert variant={'success'}>
        <CheckCircledIcon className={'w-4'} />

        <AlertTitle>
          <Trans i18nKey={'auth:verifyEmailHeading'} />
        </AlertTitle>

        <AlertDescription data-test={'email-confirmation-alert'}>
          <Trans
            i18nKey={'auth:verifyEmailBody'}
            values={{ email: props.email }}
            components={{ b: <span className={'font-semibold'} /> }}
          />
        </AlertDescription>
      </Alert>

      <p className={'text-muted-foreground text-sm'}>
        <Trans i18nKey={'auth:verifyEmailSpamHint'} />
      </p>

      <If condition={resend.isSuccess}>
        <p className={'text-sm font-medium'} role={'status'}>
          <Trans i18nKey={'auth:resendConfirmationEmailSuccess'} />
        </p>
      </If>

      <If condition={resend.isError}>
        <p className={'text-destructive text-sm font-medium'} role={'alert'}>
          <Trans i18nKey={'auth:resendConfirmationEmailError'} />
        </p>
      </If>

      <div className={'flex flex-col gap-y-2'}>
        <Button
          variant={'outline'}
          disabled={resend.isPending}
          onClick={() =>
            resend.mutate({
              email: props.email,
              emailRedirectTo: props.emailRedirectTo,
            })
          }
        >
          <If
            condition={resend.isPending}
            fallback={<Trans i18nKey={'auth:resendConfirmationEmail'} />}
          >
            <Trans i18nKey={'auth:resendingConfirmationEmail'} />
          </If>
        </Button>

        <Button variant={'link'} size={'sm'} onClick={props.onChangeEmail}>
          <Trans i18nKey={'auth:useDifferentEmail'} />
        </Button>
      </div>
    </div>
  );
}

function useResendConfirmationEmail() {
  const supabase = useSupabase();

  return useMutation({
    mutationFn: async (params: { email: string; emailRedirectTo: string }) => {
      const response = await supabase.auth.resend({
        email: params.email,
        type: 'signup',
        options: {
          emailRedirectTo: params.emailRedirectTo,
        },
      });

      if (response.error) {
        throw response.error;
      }

      return response.data;
    },
  });
}
